export interface Career {
  id: string;
  title: string;
  description: string;
  futureRelevance: number; // 1-10 scale
  emergenceTimeframe: string; // "current", "emerging", "5-10 years", "traditional"
  requiredTraits: string[];
  traditionalEquivalent?: string;
  keySkills: string[];
  educationPaths: string[];
  industryTrends: string[];
  aiRiskLevel: number; // 1-10 scale, 10 being highest risk of automation
  isTraditional?: boolean;
}

export const careers: Record<string, Career> = {
  // Future careers
  "aiEthicist": {
    id: "aiEthicist",
    title: "AI Ethics Specialist",
    description: "Guides the responsible development of artificial intelligence technologies, ensuring they align with human values and ethical principles.",
    futureRelevance: 9,
    emergenceTimeframe: "emerging",
    requiredTraits: ["analytical", "creative", "social"],
    traditionalEquivalent: "Ethics Consultant",
    keySkills: ["AI systems understanding", "ethical framework development", "policy creation", "stakeholder communication"],
    educationPaths: ["Philosophy", "Ethics", "Computer Science", "AI Certification"],
    industryTrends: ["Increased AI regulation", "focus on responsible AI", "bias mitigation"],
    aiRiskLevel: 2
  },
  
  "virtualRealityDesigner": {
    id: "virtualRealityDesigner",
    title: "Extended Reality Architect",
    description: "Creates immersive virtual and augmented reality experiences for education, training, entertainment, and remote collaboration.",
    futureRelevance: 8,
    emergenceTimeframe: "current",
    requiredTraits: ["creative", "practical", "analytical"],
    traditionalEquivalent: "UX Designer",
    keySkills: ["3D modeling", "spatial design", "user experience", "programming", "cognitive psychology"],
    educationPaths: ["Game Design", "Computer Science", "Human-Computer Interaction", "Design"],
    industryTrends: ["Meta-verse development", "remote collaboration", "immersive learning"],
    aiRiskLevel: 4
  },
  
  dataEthicist: {
    id: "dataEthicist",
    title: "Data Ethics Officer",
    description: "Oversees ethical data collection, usage, and privacy practices while balancing innovation and responsible data stewardship.",
    futureRelevance: 9,
    emergenceTimeframe: "current",
    requiredTraits: ["analytical", "social", "leadership"],
    traditionalEquivalent: "Compliance Officer",
    keySkills: ["data governance", "privacy regulations", "ethical frameworks", "stakeholder management"],
    educationPaths: ["Law", "Ethics", "Data Science", "Policy Studies"],
    industryTrends: ["Stricter privacy regulations", "personalized data ownership", "algorithmic transparency"],
    aiRiskLevel: 3
  },
  
  roboticsEngineer: {
    id: "roboticsEngineer",
    title: "Human-Robot Integration Specialist",
    description: "Designs robotic systems that work seamlessly alongside humans in various environments, from healthcare to manufacturing.",
    futureRelevance: 8,
    emergenceTimeframe: "current",
    requiredTraits: ["analytical", "practical", "creative"],
    traditionalEquivalent: "Mechanical Engineer",
    keySkills: ["robotics programming", "mechanical design", "human factors", "AI integration"],
    educationPaths: ["Robotics", "Mechanical Engineering", "Computer Science", "Human Factors Engineering"],
    industryTrends: ["Collaborative robots", "aging population assistance", "automated logistics"],
    aiRiskLevel: 3
  },

  communityHealthWorker: {
    id: "communityHealthWorker",
    title: "Precision Health Navigator",
    description: "Combines health expertise with personalized data analysis to guide individuals through preventative care and treatment options.",
    futureRelevance: 8,
    emergenceTimeframe: "emerging",
    requiredTraits: ["social", "analytical", "adaptable"],
    traditionalEquivalent: "Community Health Worker",
    keySkills: ["health data interpretation", "preventative care", "patient communication", "healthcare systems navigation"],
    educationPaths: ["Healthcare", "Public Health", "Data Science", "Psychology"],
    industryTrends: ["Personalized medicine", "preventative healthcare", "remote health monitoring"],
    aiRiskLevel: 3
  },

  "3dPrintingSpecialist": {
    id: "3dPrintingSpecialist",
    title: "Additive Manufacturing Engineer",
    description: "Specializes in designing and producing customized products using advanced 3D printing technologies across industries.",
    futureRelevance: 7,
    emergenceTimeframe: "current",
    requiredTraits: ["practical", "creative", "analytical"],
    traditionalEquivalent: "Manufacturing Technician",
    keySkills: ["3D modeling", "materials science", "manufacturing processes", "design thinking"],
    educationPaths: ["Manufacturing Engineering", "Materials Science", "Industrial Design", "Digital Fabrication"],
    industryTrends: ["Mass customization", "on-demand manufacturing", "sustainable production"],
    aiRiskLevel: 4
  },

  innovationConsultant: {
    id: "innovationConsultant",
    title: "Innovation Ecosystem Developer",
    description: "Helps organizations build environments and processes that foster continuous innovation and adaptation to rapid change.",
    futureRelevance: 8,
    emergenceTimeframe: "current",
    requiredTraits: ["leadership", "creative", "adaptable"],
    traditionalEquivalent: "Management Consultant",
    keySkills: ["innovation methodologies", "change management", "strategic foresight", "facilitation"],
    educationPaths: ["Business", "Innovation Management", "Design Thinking", "Organizational Psychology"],
    industryTrends: ["Distributed innovation", "future of work", "organizational adaptability"],
    aiRiskLevel: 3
  },

  quantumComputingAnalyst: {
    id: "quantumComputingAnalyst",
    title: "Quantum Computing Applications Specialist",
    description: "Identifies and develops practical applications for quantum computing across industries like pharmaceuticals, logistics, and cryptography.",
    futureRelevance: 8,
    emergenceTimeframe: "5-10 years",
    requiredTraits: ["analytical", "creative", "adaptable"],
    traditionalEquivalent: "Computer Systems Analyst",
    keySkills: ["quantum algorithms", "complex problem framing", "computational thinking", "domain expertise"],
    educationPaths: ["Quantum Information Science", "Computer Science", "Physics", "Mathematics"],
    industryTrends: ["Quantum supremacy", "post-quantum cryptography", "computational biology"],
    aiRiskLevel: 2
  },

  sustainabilityConsultant: {
    id: "sustainabilityConsultant",
    title: "Circular Economy Specialist",
    description: "Redesigns business operations and supply chains to eliminate waste and regenerate natural systems while maintaining profitability.",
    futureRelevance: 9,
    emergenceTimeframe: "current",
    requiredTraits: ["analytical", "creative", "leadership"],
    traditionalEquivalent: "Environmental Consultant",
    keySkills: ["systems thinking", "life cycle assessment", "sustainable design", "stakeholder engagement"],
    educationPaths: ["Environmental Science", "Sustainable Business", "Industrial Ecology", "Design"],
    industryTrends: ["Carbon neutrality", "circular supply chains", "sustainability reporting requirements"],
    aiRiskLevel: 3
  },

  remoteWorkCoordinator: {
    id: "remoteWorkCoordinator",
    title: "Distributed Workspace Architect",
    description: "Creates systems and cultures that enable effective collaboration across physical and virtual workspaces.",
    futureRelevance: 8,
    emergenceTimeframe: "current",
    requiredTraits: ["social", "adaptable", "leadership"],
    traditionalEquivalent: "Office Manager",
    keySkills: ["digital collaboration tools", "workplace psychology", "virtual team dynamics", "experience design"],
    educationPaths: ["Organizational Psychology", "Human Resources", "Workplace Design", "Communication"],
    industryTrends: ["Hybrid work models", "digital workplace tools", "global talent acquisition"],
    aiRiskLevel: 5
  },

  predictiveHealthcareAnalyst: {
    id: "predictiveHealthcareAnalyst",
    title: "Healthcare Predictive Analytics Specialist",
    description: "Applies advanced data analysis to predict health outcomes and recommend personalized preventative measures.",
    futureRelevance: 9,
    emergenceTimeframe: "emerging",
    requiredTraits: ["analytical", "practical", "social"],
    traditionalEquivalent: "Healthcare Analyst",
    keySkills: ["health data analysis", "predictive modeling", "medical knowledge", "patient communications"],
    educationPaths: ["Health Informatics", "Data Science", "Public Health", "Medicine"],
    industryTrends: ["Preventative healthcare", "personalized medicine", "wearable health monitoring"],
    aiRiskLevel: 3
  },

  humanAiCollaborationManager: {
    id: "humanAiCollaborationManager",
    title: "Human-AI Collaboration Manager",
    description: "Designs workflows and teams that optimize the collaboration between human workers and AI systems.",
    futureRelevance: 9,
    emergenceTimeframe: "emerging",
    requiredTraits: ["leadership", "adaptable", "social"],
    traditionalEquivalent: "Operations Manager",
    keySkills: ["AI systems understanding", "workflow design", "change management", "human factors"],
    educationPaths: ["Business", "Human-Computer Interaction", "Organizational Psychology", "AI Studies"],
    industryTrends: ["AI augmentation", "workplace transformation", "continuous reskilling"],
    aiRiskLevel: 2
  },

  renewableEnergyTechnician: {
    id: "renewableEnergyTechnician",
    title: "Renewable Energy Systems Integrator",
    description: "Designs and implements integrated renewable energy systems for homes, communities, and businesses.",
    futureRelevance: 9,
    emergenceTimeframe: "current",
    requiredTraits: ["practical", "analytical", "adaptable"],
    traditionalEquivalent: "Electrician",
    keySkills: ["renewable technologies", "electrical systems", "energy storage", "system optimization"],
    educationPaths: ["Electrical Engineering", "Renewable Energy", "Technical Certification", "Environmental Science"],
    industryTrends: ["Distributed energy generation", "energy independence", "electrification"],
    aiRiskLevel: 4
  },

  biomedicalTechnician: {
    id: "biomedicalTechnician",
    title: "Biomedical Technology Specialist",
    description: "Maintains and operates increasingly complex medical devices and systems, including remotely operated and AI-augmented equipment.",
    futureRelevance: 8,
    emergenceTimeframe: "emerging",
    requiredTraits: ["practical", "analytical", "adaptable"],
    traditionalEquivalent: "Medical Equipment Technician",
    keySkills: ["biomedical technologies", "troubleshooting", "preventative maintenance", "technology integration"],
    educationPaths: ["Biomedical Technology", "Engineering", "Healthcare Technology Management"],
    industryTrends: ["Remote diagnostics", "AI-assisted healthcare", "personalized medical devices"],
    aiRiskLevel: 5
  },

  digitalEducationSpecialist: {
    id: "digitalEducationSpecialist",
    title: "Immersive Learning Experience Designer",
    description: "Creates personalized, adaptive learning experiences using emerging technologies like AR/VR and adaptive learning algorithms.",
    futureRelevance: 8,
    emergenceTimeframe: "emerging",
    requiredTraits: ["creative", "social", "analytical"],
    traditionalEquivalent: "Instructional Designer",
    keySkills: ["learning experience design", "educational technology", "cognitive science", "content development"],
    educationPaths: ["Educational Technology", "Instructional Design", "Learning Sciences", "UX Design"],
    industryTrends: ["Adaptive learning", "micro-credentials", "lifelong learning platforms"],
    aiRiskLevel: 4
  },

  cybersecuritySpecialist: {
    id: "cybersecuritySpecialist",
    title: "Cyber Resilience Architect",
    description: "Designs systems that not only defend against cyber threats but can quickly recover and adapt after attacks.",
    futureRelevance: 9,
    emergenceTimeframe: "current",
    requiredTraits: ["analytical", "adaptable", "practical"],
    traditionalEquivalent: "Network Security Specialist",
    keySkills: ["threat modeling", "security architecture", "incident response", "resilience planning"],
    educationPaths: ["Cybersecurity", "Computer Science", "Information Security", "Risk Management"],
    industryTrends: ["Zero-trust architectures", "AI-driven threats", "security automation"],
    aiRiskLevel: 2
  },

  futureOfWorkStrategist: {
    id: "futureOfWorkStrategist",
    title: "Workplace Evolution Strategist",
    description: "Helps organizations navigate workforce transformations due to automation, changing work preferences, and skill evolution.",
    futureRelevance: 8,
    emergenceTimeframe: "current",
    requiredTraits: ["leadership", "creative", "social"],
    traditionalEquivalent: "HR Strategist",
    keySkills: ["workforce planning", "change management", "future thinking", "skills mapping"],
    educationPaths: ["Human Resources", "Organizational Development", "Future Studies", "Business"],
    industryTrends: ["Skills-based organizations", "continuous learning", "workforce augmentation"],
    aiRiskLevel: 3
  },

  smartCityPlanningSpecialist: {
    id: "smartCityPlanningSpecialist",
    title: "Smart Infrastructure Designer",
    description: "Plans and implements connected, data-driven infrastructure systems for more efficient and sustainable urban environments.",
    futureRelevance: 8,
    emergenceTimeframe: "emerging",
    requiredTraits: ["analytical", "practical", "creative"],
    traditionalEquivalent: "Urban Planner",
    keySkills: ["urban systems", "IoT technologies", "data analysis", "sustainable design"],
    educationPaths: ["Urban Planning", "Civil Engineering", "Data Science", "Smart City Technologies"],
    industryTrends: ["Connected infrastructure", "data-driven governance", "sustainable urban development"],
    aiRiskLevel: 4
  },

  telemedicalDirector: {
    id: "telemedicalDirector",
    title: "Telehealth Systems Director",
    description: "Oversees virtual healthcare delivery systems, integrating physical care with remote diagnostics and treatment.",
    futureRelevance: 8,
    emergenceTimeframe: "current",
    requiredTraits: ["leadership", "adaptable", "analytical"],
    traditionalEquivalent: "Medical Director",
    keySkills: ["healthcare systems", "telehealth technologies", "clinical workflows", "patient experience"],
    educationPaths: ["Medicine", "Healthcare Administration", "Health Informatics", "Public Health"],
    industryTrends: ["Remote care delivery", "health monitoring technologies", "healthcare accessibility"],
    aiRiskLevel: 3
  },

  experienceDesigner: {
    id: "experienceDesigner",
    title: "Multisensory Experience Designer",
    description: "Creates immersive, meaningful experiences that engage multiple senses across physical and digital environments.",
    futureRelevance: 7,
    emergenceTimeframe: "emerging",
    requiredTraits: ["creative", "social", "practical"],
    traditionalEquivalent: "UX Designer",
    keySkills: ["multisensory design", "cognitive psychology", "storytelling", "environmental design"],
    educationPaths: ["Experience Design", "Psychology", "Interaction Design", "Multimedia Arts"],
    industryTrends: ["Mixed reality experiences", "sensory engagement", "experience economy"],
    aiRiskLevel: 3
  },

  sustainabilityLeader: {
    id: "sustainabilityLeader",
    title: "Regenerative Business Transformer",
    description: "Leads organizations in shifting from sustainable practices to regenerative ones that actively improve environmental and social conditions.",
    futureRelevance: 9,
    emergenceTimeframe: "emerging",
    requiredTraits: ["leadership", "analytical", "creative"],
    traditionalEquivalent: "Sustainability Director",
    keySkills: ["systems thinking", "organizational change", "regenerative design", "stakeholder engagement"],
    educationPaths: ["Sustainability", "Regenerative Economics", "Leadership", "Environmental Science"],
    industryTrends: ["Regenerative business models", "climate positive operations", "stakeholder capitalism"],
    aiRiskLevel: 2
  },

  aiContentCreator: {
    id: "aiContentCreator",
    title: "Human-AI Creative Director",
    description: "Guides AI systems in creating authentic, engaging content while maintaining human creative direction and ethical standards.",
    futureRelevance: 8,
    emergenceTimeframe: "emerging",
    requiredTraits: ["creative", "adaptable", "leadership"],
    traditionalEquivalent: "Creative Director",
    keySkills: ["AI tools mastery", "creative direction", "ethical content guidelines", "brand storytelling"],
    educationPaths: ["Creative Writing", "Digital Media", "AI in Creative Fields", "Communication"],
    industryTrends: ["AI-assisted creation", "personalized content", "content authenticity"],
    aiRiskLevel: 3
  },

  bioartist: {
    id: "bioartist",
    title: "Bioartist & Designer",
    description: "Creates with living materials and biological processes, exploring the intersection of nature, technology, and design.",
    futureRelevance: 7,
    emergenceTimeframe: "5-10 years",
    requiredTraits: ["creative", "analytical", "adaptable"],
    traditionalEquivalent: "Industrial Designer",
    keySkills: ["biotechnology basics", "design thinking", "materials science", "artistic expression"],
    educationPaths: ["Bioart", "Design", "Biology", "New Media Arts"],
    industryTrends: ["Sustainable materials", "living design", "nature-inspired solutions"],
    aiRiskLevel: 3
  },
  
  // Traditional careers that will remain relevant
  "physician": {
    id: "physician",
    title: "Medical Doctor",
    description: "Diagnoses and treats illnesses, injuries, and other health conditions while building trust and human connection with patients.",
    futureRelevance: 8,
    emergenceTimeframe: "traditional",
    requiredTraits: ["analytical", "social", "adaptable"],
    keySkills: ["clinical diagnosis", "patient communication", "critical thinking", "lifelong learning"],
    educationPaths: ["Medicine", "Biology", "Continuing Medical Education"],
    industryTrends: ["telemedicine integration", "precision medicine", "preventative care"],
    aiRiskLevel: 3,
    isTraditional: true
  },
  
  "teacher": {
    id: "teacher",
    title: "Educator",
    description: "Guides learning and development while adapting to new technologies and personalized education approaches.",
    futureRelevance: 7,
    emergenceTimeframe: "traditional",
    requiredTraits: ["social", "creative", "adaptable"],
    keySkills: ["instructional design", "social-emotional learning", "adaptive teaching", "technology integration"],
    educationPaths: ["Education", "Subject Specialty", "Educational Technology"],
    industryTrends: ["blended learning", "personalized education", "skills-based curriculum"],
    aiRiskLevel: 4,
    isTraditional: true
  },
  
  "therapist": {
    id: "therapist",
    title: "Mental Health Therapist",
    description: "Provides counseling and treatment for mental health conditions with a focus on human connection and personalized care.",
    futureRelevance: 8,
    emergenceTimeframe: "traditional",
    requiredTraits: ["social", "adaptable", "analytical"],
    keySkills: ["active listening", "therapeutic techniques", "emotional intelligence", "crisis management"],
    educationPaths: ["Psychology", "Social Work", "Mental Health Counseling"],
    industryTrends: ["digital therapy tools", "increased accessibility", "preventative mental health"],
    aiRiskLevel: 2,
    isTraditional: true
  },
  
  "softwareEngineer": {
    id: "softwareEngineer",
    title: "Software Engineer",
    description: "Designs and builds software solutions while continuously adapting to new technologies and development approaches.",
    futureRelevance: 9,
    emergenceTimeframe: "traditional",
    requiredTraits: ["analytical", "creative", "adaptable"],
    keySkills: ["programming", "system design", "problem-solving", "continuous learning"],
    educationPaths: ["Computer Science", "Software Engineering", "Bootcamps", "Self-learning"],
    industryTrends: ["AI integration", "low-code development", "distributed systems"],
    aiRiskLevel: 4,
    isTraditional: true
  },
  
  "nurse": {
    id: "nurse",
    title: "Nursing Professional",
    description: "Provides direct patient care with a focus on human compassion and increasing technical expertise.",
    futureRelevance: 8,
    emergenceTimeframe: "traditional",
    requiredTraits: ["social", "practical", "adaptable"],
    keySkills: ["patient care", "medical knowledge", "communication", "adaptability"],
    educationPaths: ["Nursing", "Healthcare", "Specialization Certifications"],
    industryTrends: ["technology-enhanced care", "telehealth integration", "preventative focus"],
    aiRiskLevel: 2,
    isTraditional: true
  },
  
  "electrician": {
    id: "electrician",
    title: "Electrical Systems Specialist",
    description: "Installs and maintains increasingly complex electrical and smart home systems.",
    futureRelevance: 7,
    emergenceTimeframe: "traditional",
    requiredTraits: ["practical", "analytical", "adaptable"],
    keySkills: ["technical knowledge", "problem-solving", "safety protocols", "new technology adaptation"],
    educationPaths: ["Vocational Training", "Apprenticeship", "Electrical Engineering"],
    industryTrends: ["renewable energy integration", "smart systems", "IoT connectivity"],
    aiRiskLevel: 3,
    isTraditional: true
  },
  
  "lawyer": {
    id: "lawyer",
    title: "Legal Professional",
    description: "Provides legal counsel and advocacy with increasing technological and analytical support.",
    futureRelevance: 7,
    emergenceTimeframe: "traditional",
    requiredTraits: ["analytical", "social", "leadership"],
    keySkills: ["legal analysis", "negotiation", "communication", "research"],
    educationPaths: ["Law", "Pre-law", "Specialized Legal Training"],
    industryTrends: ["legal tech integration", "specialized expertise", "global legal frameworks"],
    aiRiskLevel: 5,
    isTraditional: true
  },
  
  "chef": {
    id: "chef",
    title: "Culinary Arts Professional",
    description: "Creates food experiences while adapting to new technologies, dietary trends, and sustainability practices.",
    futureRelevance: 6,
    emergenceTimeframe: "traditional",
    requiredTraits: ["creative", "practical", "adaptable"],
    keySkills: ["culinary techniques", "menu development", "food science", "sustainability practices"],
    educationPaths: ["Culinary Arts", "Food Science", "Nutrition"],
    industryTrends: ["sustainable cuisine", "food technology", "experience dining"],
    aiRiskLevel: 4,
    isTraditional: true
  }
};

export const getEmergingCareers = () => {
  return Object.values(careers).filter(
    career => career.emergenceTimeframe === "5-10 years" || career.emergenceTimeframe === "emerging"
  );
};

export const getTraditionalCareers = () => {
  return Object.values(careers).filter(
    career => career.isTraditional === true
  );
};

export const getTransformingCareers = () => {
  return Object.values(careers).filter(
    career => career.traditionalEquivalent && career.aiRiskLevel > 5
  );
};

export const getCareerMatchesForType = (typeId: string, includeTraditional = true) => {
  const careerIds = personalityTypes.find(type => type.id === typeId)?.careerMatches || [];
  let matches = careerIds.map(id => careers[id]).filter(Boolean);
  
  // Add some traditional careers based on traits
  if (includeTraditional) {
    const personalityType = personalityTypes.find(type => type.id === typeId);
    if (personalityType) {
      // Find top traits for this personality type
      const topTraits = Object.entries(personalityType.traits)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 2)
        .map(entry => entry[0]);
      
      // Find traditional careers matching these traits
      const traditionalMatches = getTraditionalCareers()
        .filter(career => 
          career.requiredTraits.some(trait => topTraits.includes(trait))
        )
        .slice(0, 2); // Limit to 2 traditional careers
      
      matches = [...matches, ...traditionalMatches];
    }
  }
  
  return matches;
};

export const getTopFutureCareers = (count = 5) => {
  return Object.values(careers)
    .sort((a, b) => b.futureRelevance - a.futureRelevance)
    .slice(0, count);
};

import { personalityTypes } from './personalityTypes';
